import { useState } from 'react'
import { useMutation, useReactiveVar } from '@apollo/client'
import type { Adapter } from '@solana/wallet-adapter-base'
import { useWallet } from '@solana/wallet-adapter-react'
import { GET_ME } from 'api/auth/me'
import { LINK_WALLET } from 'api/wallets/link-wallet'
import { LINK_WALLET_CHALLENGE } from 'api/wallets/link-wallet-challenge'
import {
  connectSelectedSolanaWallet,
  isUserRejectedWalletError,
  signChallengeWithAdapter
} from 'features/auth/utils/connect-solana-adapter'
import type { SolanaConnectorProvider } from 'features/auth/utils/get-solana-connector-providers'
import { usePlayWallet } from 'shared/hooks/use-play-wallet'
import { choosePlayWallet } from 'shared/store/play-wallet'
import { userVar } from 'shared/store/user'
import { notifyLinkWalletError, notifyWalletLinked } from '../utils/notify'

/**
 * Links a Solana wallet to the signed-in account: the wallet signs a XYRO
 * challenge and the backend records it as one of the user's wallets. A wallet
 * that already belongs to another account (including an auto-created
 * Anonymous one) is rejected by the backend and nothing changes.
 */
// eslint-disable-next-line max-statements
export const useLinkWallet = () => {
  const [requestChallenge] = useMutation(LINK_WALLET_CHALLENGE)
  const [commitLinkWallet] = useMutation(LINK_WALLET, {
    refetchQueries: [GET_ME],
    awaitRefetchQueries: true
  })
  const { wallet, wallets, select } = useWallet()
  const user = useReactiveVar(userVar)
  const { linkedAddresses, selectWallet } = usePlayWallet()
  const [isLinking, setIsLinking] = useState(false)

  // eslint-disable-next-line max-statements
  const linkAdapterWallet = async (adapter: Adapter, walletAddress: string) => {
    if (linkedAddresses.includes(walletAddress)) {
      selectWallet(walletAddress)
      return
    }

    const { data } = await requestChallenge({ variables: { walletAddress } })
    const challenge = data?.linkWalletChallenge.challenge
    if (!challenge) throw new Error('Failed to get a challenge to sign')

    const signature = await signChallengeWithAdapter(adapter, challenge)
    const { data: linked } = await commitLinkWallet({
      variables: { walletAddress, signature }
    })
    if (!linked || !user) throw new Error('Wallet was not linked')

    // The refetched user may not have reached `linkedAddresses` yet, so the
    // choice is stored directly; the sync keeps it once the wallet is listed.
    choosePlayWallet(user.id, linked.linkWallet.address)
    notifyWalletLinked(linked.linkWallet.address)
  }

  const runLinking = async (link: () => Promise<void>) => {
    if (isLinking) return

    setIsLinking(true)
    try {
      await link()
    } catch (error) {
      if (!isUserRejectedWalletError(error)) notifyLinkWalletError(error)
    } finally {
      setIsLinking(false)
    }
  }

  /** Links the wallet that is connected right now. */
  const linkConnectedWallet = () =>
    runLinking(async () => {
      const adapter = wallet?.adapter
      const walletAddress = adapter?.publicKey?.toBase58()
      if (!adapter || !walletAddress) throw new Error('Wallet not connected')

      await linkAdapterWallet(adapter, walletAddress)
    })

  /**
   * Re-authorizes the account currently active in the wallet app (so the user
   * can switch accounts there first) and links it.
   */
  const linkProviderWallet = (provider: SolanaConnectorProvider) =>
    runLinking(async () => {
      const connected = await connectSelectedSolanaWallet(
        wallets,
        provider.walletName,
        select
      )

      await linkAdapterWallet(connected.adapter, connected.walletAddress)
    })

  return { linkConnectedWallet, linkProviderWallet, isLinking }
}
